import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  STRING_SCREEN_APPLY_NOW,
  STRING_SCREEN_BUILD_PROFILE,
  STRING_SCREEN_BUILD_PROFILE_OR_RESUME_DESCRIPTION,
  STRING_SCREEN_DONT_HAVE_RESUME,
  STRING_SCREEN_PARSING_RESUME,
  STRING_SCREEN_SKIP_STEP,
  STRING_SCREEN_UPLOAD_RESUME,
  STRING_SCREEN_UPLOAD_RESUME_DESCRIPTION,
} from '@zr/jobseeker-public/apps/jobseeker-screens/labels';

import LoadingAnimation from '@zr/components/LoadingAnimation';
import ResumeUpload from '@zr/components/ResumeUpload';

import ResumeGraphic from './ResumeGraphic';

import useUploadAndParseResume from './useUploadAndParseResume';
import { combineStrings } from './utilities';

import './ScreenViewWrapper.less';
import './ResumeProfileStart.less';

const defaultStrings = {
  title: STRING_SCREEN_APPLY_NOW,
  description_mobile: STRING_SCREEN_BUILD_PROFILE_OR_RESUME_DESCRIPTION,
  description_desktop: STRING_SCREEN_UPLOAD_RESUME_DESCRIPTION,
  cta_build_profile: STRING_SCREEN_BUILD_PROFILE,
  cta_no_resume: STRING_SCREEN_DONT_HAVE_RESUME,
  cta_skip: STRING_SCREEN_SKIP_STEP,
  cta_upload_resume: STRING_SCREEN_UPLOAD_RESUME,
  loading_parsing: STRING_SCREEN_PARSING_RESUME,
};

export function ResumeProfileStart({
  autologin, disabled, isCheckIn, isMobile, method, onBuildProfileContinue,
  onNoResumeContinue, onResumeContinueWithResult, onSkip, resumeRequired, setIsLoading, strings,
}) {
  const labels = useMemo(() => combineStrings(strings, defaultStrings), [strings]);

  const {
    isLoading,
    handleUploadStart,
    handleUploadEnd,
    handleUploadError,
  } = useUploadAndParseResume(onResumeContinueWithResult, autologin, isCheckIn);

  useEffect(() => {
    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  // Set CSS classes
  const additionalClasses = classNames({
    mobile: isMobile,
    desktop: !isMobile,
    can_skip: !resumeRequired,
  });

  return (
    <div className={`ScreenViewWrapper ResumeProfileStart ${additionalClasses}`}>
      {!isLoading && (
        <>
          <h4 className="title">{labels.title}</h4>
          <p className="description">{isMobile ? labels.description_mobile : labels.description_desktop}</p>
          <div className="resume_img_wrapper">
            <ResumeGraphic />
          </div>
          <div className="button_wrapper">
            {!!isMobile && (
              <button
                className="build_profile_button"
                onClick={resumeRequired ? onNoResumeContinue : onBuildProfileContinue}
                type="button"
              >
                {labels.cta_build_profile}
              </button>
            )}
            <ResumeUpload
              disabled={disabled}
              labelClassName="upload_resume_button"
              method={method}
              onUploadError={handleUploadError}
              onUploadEnd={handleUploadEnd}
              onUploadStart={handleUploadStart}
              overwrite
              replaceDefault
              text={labels.cta_upload_resume}
              useResumePredictorValidation
            />
            {!isMobile && (
              <button
                className="build_profile_button"
                onClick={resumeRequired ? onNoResumeContinue : onBuildProfileContinue}
                type="button"
              >
                {labels.cta_no_resume}
              </button>
            )}
            {!resumeRequired && !isCheckIn && (
              <button
                className="alt_button"
                onClick={onSkip}
                type="button"
              >
                {labels.cta_skip}
              </button>
            )}
          </div>
        </>
      )}
      {isLoading && (
        <LoadingAnimation loadingMessage={labels.loading_parsing} />
      )}
    </div>
  );
}

ResumeProfileStart.propTypes = {
  autologin: PropTypes.bool,
  disabled: PropTypes.bool,
  isCheckIn: PropTypes.bool,
  isMobile: PropTypes.bool,
  method: PropTypes.string,
  onBuildProfileContinue: PropTypes.func.isRequired,
  onNoResumeContinue: PropTypes.func.isRequired,
  onResumeContinueWithResult: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  resumeRequired: PropTypes.bool,
  setIsLoading: PropTypes.func,
  strings: PropTypes.object,
};

ResumeProfileStart.defaultProps = {
  autologin: false,
  disabled: false,
  isCheckIn: false,
  isMobile: false,
  method: null,
  resumeRequired: false,
  setIsLoading: () => {},
  strings: undefined,
};

export default ResumeProfileStart;
